import { z } from "zod";
import { MiDetailFields, MiModal } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";

const INITIAL_VALUES = {
  name: "",
  abbreviation: "",
  isPostConstruction: false,
  targetCycleDays: "",
};

export const AddUpdateMilestoneModal = ({
  onModalClose,
  onSubmit,
  selectedData,
  loading,
  open,
}) => {
  const templateFields = [
    {
      label: "Milestone Name",
      name: "name",
      width: "100%",
      validationRule: z
        .string()
        .min(1, "Milestone name is required.")
        .max(50, "Milestone name must be at most 50 characters."),
    },
    {
      label: "Abbreviation",
      name: "abbreviation",
      width: "100%",
      validationRule: z
        .string()
        .max(10, "Abbreviation must be at most 10 characters."),
    },
    {
      label: "Is Post Construction",
      name: "isPostConstruction",
      fieldType: INPUT_TYPES.CHECKBOX,
      width: "100%",
      validationRule: z.boolean(),
    },
    {
      label: "Target Cycle Days",
      name: "targetCycleDays",
      required: true,
      fieldType: INPUT_TYPES.NUMBER,
      width: "100%",
      validationRule: z
        .number({
          required_error: "This field is required",
          invalid_type_error: "Please enter a valid number",
        })
        .int()
        .refine((val) => val >= 0, { message: "Value must be at least 0" })
        .refine((val) => val <= 365, { message: "Value must be at most 365" }),
    },
  ];
  const edit = useEdit(selectedData ?? INITIAL_VALUES, templateFields);

  const handleModalClose = () => {
    onModalClose();
    edit.reset();
  };

  const handleSubmit = () => {
    onSubmit({
      name: edit.getValue("name"),
      abbreviation: edit.getValue("abbreviation"),
      isPostConstruction: edit.getValue("isPostConstruction"),
      targetCycleDays: edit.getValue("targetCycleDays"),
    });
    edit.reset();
  };

  return (
    <MiModal
      open={open}
      closeModal={handleModalClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleModalClose,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: handleSubmit,
          disabled: !edit.isDirty() || !edit.isValid(),
        },
      ]}
      loading={loading}
      title={selectedData?.name ? "Edit Milestone" : "Add Milestone"}
      topRightClose={edit.reset}
    >
      <MiDetailFields detailFields={templateFields} edit={edit} />
    </MiModal>
  );
};
